import { PropsWithChildren, ReactNode, useMemo, useRef } from "react";
import { useLocSetting } from "../../hooks/SettingsHooks";
import { AnimatePresence, motion } from "motion/react";
import classNames from "classnames";
import "./style.scss";
import Icon from "../Icon";
import { useUiMessageContext } from "../UIMessages/context";
import { ERROR, getIcon, INFO, SUCCESS, WARN } from "../UIMessages";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { useCssVariableContext } from "../../functional-components/CssVariableContext";
import { UiMessage, UiMessageButton } from "../../types/UiMessage";
import { SettingsMessage } from "../../types/Settings";
import useResizeObserver from "@react-hook/resize-observer";
import BjelinButton from "../BjelinButton";
import { useUser } from "../../functional-components/UserContext";

type BannerMessage = UiMessage & {
  b2bOnly: boolean;
  position: "top" | "checkout";
};

export const useActiveBannerMessages = (): BannerMessage[] => {
  const { getDismissedBannerMessages } = useUiMessageContext();
  const banners: SettingsMessage[] = useLocSetting("banners");
  const dismissedMessages = getDismissedBannerMessages();

  const bannerMessages = [];
  for (const cockpitData of banners) {
    if (cockpitData.type === "hidden") {
      continue;
    }

    const banner: BannerMessage = {
      b2bOnly: cockpitData.b2bOnly === true,
      text: cockpitData.message,
      type: cockpitData.type,
      position: cockpitData.position || "top",
      link_url: cockpitData.link_url
    };
    if (!cockpitData.hash) {
      banner.identifier =
        cockpitData.message + cockpitData.type + (cockpitData.link_url || "");
    } else {
      banner.identifier = cockpitData.hash;
    }

    if (
      banner &&
      banner.type &&
      banner.text &&
      !dismissedMessages.includes(banner.identifier)
    ) {
      bannerMessages.push(banner);
    }
  }
  return bannerMessages;
};

const MessageBannerMessage = (message: BannerMessage) => {
  const { dismissBannerMessage, getDismissedBannerMessages } =
    useUiMessageContext();
  const t = useBjelinTranslations();
  const { b2b } = useUser();

  let identifier: string;
  if (!message.identifier) {
    identifier = message.text;
  } else {
    identifier = message.identifier;
  }

  const buttons = useMemo(() => {
    const extraButtons: UiMessageButton[] = [];
    if (message.link_url) {
      extraButtons.push({
        text: t("ui-message.read-more"),
        url: message.link_url,
        type: "secondary"
      });
    }
    extraButtons.push({
      text: t("ui-message.dismiss"),
      onClick: () => dismissBannerMessage(identifier),
      type: "secondary",
      icon: <Icon name="xmark" />,
      iconPosition: "before",
      className: "dismiss-btn"
    });
    return (message.buttons || []).concat(extraButtons);
  }, [dismissBannerMessage, identifier, message.buttons, message.link_url, t]);

  const BannerButton = (button: UiMessageButton) => {
    return (
      <BjelinButton
        key={button.text}
        onClick={button.onClick}
        url={button.url}
        className={button.className}
        color={
          button.type === "dangerous"
            ? "danger"
            : button.type === "primary"
            ? "light"
            : "link"
        }
        size={"small"}
        outline={button.type === "primary"}
      >
        {button.icon && button.iconPosition === "before" && (
          <span className={"button-icon before"}>{button.icon}</span>
        )}
        {button.text}
        {button.icon && button.iconPosition === "after" && (
          <span className={"button-icon after"}>{button.icon}</span>
        )}
      </BjelinButton>
    );
  };

  const isDismissed = getDismissedBannerMessages().includes(identifier);
  const includeB2bOnlyMessage = !message.b2bOnly || b2b;
  return (
    <AnimatePresence>
      {!isDismissed && includeB2bOnlyMessage && (
        <motion.div
          role="alert"
          key={identifier}
          initial={{ opacity: 0, y: -100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, height: 0, transition: { duration: 0.25 } }}
          className={classNames("bannerMessage", {
            [SUCCESS]: message.type === SUCCESS,
            [WARN]: message.type === WARN,
            [ERROR]: message.type === ERROR,
            [INFO]: message.type === INFO
          })}
        >
          <div className="msg-content">
            {message.type && !message.icon && (
              <div className={"msg-icon"}>
                <Icon name={getIcon(message.type)} />
              </div>
            )}
            {message.icon && <div className={"msg-icon"}>{message.icon}</div>}
            <div className={"msg-text"}>{message.text}</div>
          </div>
          {buttons && buttons.length > 0 && (
            <div className="msg-buttons">
              {buttons.map((button, index) => (
                <BannerButton key={index} {...button} />
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Displays banner messages:
 * - active message from site settings
 * - UIMessages with banner flag
 */
const MessageBanner = ({ children }: PropsWithChildren): ReactNode => {
  const { getMessages } = useUiMessageContext();
  const { setMessageBannerHeight } = useCssVariableContext();
  const banners = useActiveBannerMessages();
  const ref = useRef(null);
  useResizeObserver(ref, entry =>
    setMessageBannerHeight(entry?.contentRect?.height || 0)
  );

  const messages = useMemo(
    () =>
      banners
        .filter(b => b.position === "top")
        .concat(
          getMessages()
            .filter(msg => msg.banner)
            .map(m => ({
              ...m,
              b2bOnly: false,
              position: "top"
            }))
        ),
    [banners, getMessages]
  );

  return (
    <div className="messageBanner" ref={ref}>
      {messages?.length > 0 &&
        messages.map(msg => (
          <MessageBannerMessage
            {...msg}
            position={"top"}
            key={msg.identifier}
          />
        ))}
      {children}
    </div>
  );
};

export default MessageBanner;
